// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { type WeightUnitPlusTrays, displayCurrency, formatTime, getCurrencySymbol } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Checkbox, Collapse, FormControlLabel, IconButton } from '../../lib/base';
import { KeyboardArrowDownIcon, KeyboardArrowUpIcon } from '../../lib/icons';
import { formatOrderInput } from '../../utils';

interface OrderContainerData {
  readonly id: number;
  readonly order_fulfillment_time: number;
  readonly customer: string;
  readonly inputs: {
    readonly id: number;
    readonly price_per_item?: number;
    readonly percent_discount_at_creation?: number;
    readonly quantity: string;
    readonly size_amount?: string;
    readonly unit: string;
  }[];
}

export default function SelectOrdersContainer({
  orders,
  timezone,
  selectedOrders,
  setSelectedOrders,
  expandedOrderIds,
  setExpandedOrderIds,
}: {
  readonly orders: OrderContainerData[];
  readonly timezone: string;
  readonly selectedOrders: number[];
  readonly setSelectedOrders: (orders: number[]) => void;
  readonly expandedOrderIds: Set<number>;
  readonly setExpandedOrderIds: (ids: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
}) {
  const ordersWithFormats = orders.map((order) => ({
    ...order,
    formattedTime: formatTime(order.order_fulfillment_time, { timezone }),
    formattedId: order.id,
  }));

  const ordersWithLabels = ordersWithFormats.map((order) => ({
    ...order,
    label: `Order ${order.formattedId} (${order.customer}) - ${order.formattedTime}`,
  }));

  return (
    <ul>
      {ordersWithLabels.map((order) => {
        return (
          <SelectOrder
            isSelected={selectedOrders.includes(order.id)}
            onSelectOrder={(isSelected) => {
              const updatedSelectedOrders = isSelected
                ? [...selectedOrders, order.id]
                : selectedOrders.filter((id) => id !== order.id);
              setSelectedOrders(updatedSelectedOrders);
            }}
            key={order.id}
            order={order}
            label={order.label}
            open={expandedOrderIds.has(order.id)}
            setOpen={(open: boolean) => {
              setExpandedOrderIds((prev) => {
                const set = new Set(prev);
                if (open) {
                  set.add(order.id);
                } else {
                  set.delete(order.id);
                }
                return set;
              });
            }}
          />
        );
      })}
    </ul>
  );
}

function SelectOrder({
  order,
  label,
  isSelected,
  onSelectOrder,
  open,
  setOpen,
}: {
  readonly order: OrderContainerData;
  readonly label: string;
  readonly isSelected: boolean;
  readonly onSelectOrder: (isSelected: boolean) => void;
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
}) {
  const { currencyCode } = useUserContext();
  const currencySymbol = getCurrencySymbol(currencyCode);
  const onChangeSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectOrder(e.target.checked);
  };

  const hasInputs = order.inputs.length > 0;

  return (
    <li key={order.id}>
      <Box display="flex" flexDirection="row" alignItems="center">
        <FormControlLabel
          sx={{ margin: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
          label={label}
          control={<Checkbox checked={isSelected} onChange={onChangeSelected} />}
          labelPlacement="start"
        />
        {hasInputs && (
          <IconButton aria-label="show-details" sx={{ ml: 1 }} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </Box>
      {hasInputs && (
        <Collapse
          in={open}
          unmountOnExit
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box>
            <ul>
              {order.inputs.map((input) => {
                const inputStr = formatOrderInput({
                  quantity: input.quantity,
                  sizeAmount: input.size_amount,
                  sizeUnit: input.unit as WeightUnitPlusTrays,
                });
                return (
                  <li key={input.id}>
                    {inputStr} at{' '}
                    {input.price_per_item != null
                      ? `${displayCurrency({
                          currencyCode,
                          amount: input.price_per_item,
                        })}`
                      : `${currencySymbol} -`}{' '}
                    each
                    {input.percent_discount_at_creation ? ` with ${input.percent_discount_at_creation}% discount` : ''}
                  </li>
                );
              })}
            </ul>
          </Box>
        </Collapse>
      )}
    </li>
  );
}
